/**
 * 自定义hook
 */

import React, { useState, useRef, useEffect, ChangeEvent, useLayoutEffect } from 'react';
import qs, { ParsedQuery } from 'query-string';
import { service } from 'service/index';
import { bridge } from 'util/index';

export * from './pay';

/**
 * 自动绑定input的change事件并更新值
 * @param defaultValue {string}
 */
export function useInputBind(defaultValue: string | (() => string)) {
  const [value, setValue] = useState(defaultValue);
  const changeRef = useRef(function (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
    setValue(e.target.value);
  });
  return {
    value,
    onChange: changeRef.current,
  };
}

interface UseAsyncArg<T> {
  // 实际的异步操作函数
  fn: () => Promise<T>;
  // 默认是否为加载中
  defaultIsLoading?: boolean;
  // 默认数据
  defaultData: T;
  // 每次请求时，是否重置数据为默认的
  resetOnRequest?: boolean;
}

export function useAsync<T = any>({ fn, defaultIsLoading = false, defaultData, resetOnRequest = true }: UseAsyncArg<T>) {
  const [loading, setLoading] = useState(defaultIsLoading);
  const [data, setData] = useState<T>(defaultData);

  function handle(force = false) {
    if (loading && !force) {
      alert('操作太快，请稍后再试');
      return;
    }
    setLoading(true);
    if (resetOnRequest) {
      setData(defaultData);
    }
    fn()
      .then(function (data: T) {
        setData(data);
        setLoading(false);
      })
      .catch(function (err: Error) {
        setData(defaultData);
        setLoading(false);
        alert(err.message);
      });
  }

  // 处理初次就自动请求数据的情况
  useEffect(function () {
    if (defaultIsLoading) {
      handle(true);
    }
  }, []);

  return {
    loading,
    data,
    // 修改数据的方法也暴露出去，外部可能需要主动修改数据
    setData,
    handle,
  };
}

type UseRequestArg<T> = { url: string; method: 'GET' | 'POST'; data?: any; } & Pick<UseAsyncArg<T>, 'defaultIsLoading' | 'defaultData' | 'resetOnRequest'>

/**
 * 封装基于axios的异步请求hook
 * @param args
 */
export function useRequest<T>(args: UseRequestArg<T>) {
  const {
    url,
    method,
    data,
  } = args;
  return useAsync<T>({
    defaultData: args.defaultData,
    defaultIsLoading: args.defaultIsLoading,
    resetOnRequest: args.resetOnRequest !== false,
    fn: function () {
      let p;
      if (method === 'GET') {
        p = service.get(url);
      } else {
        p = service.post(url, data);
      }
      return p.then(function (res) {
        if (res.data.status === 0) {
          return res.data.data;
        } else {
          throw new Error(res.data.message);
        }
      });
    },
  });
}

/**
 * 设置浏览器title
 * @param title
 */
export function useBrowserTitle(title: string) {
  useEffect(function () {
    document.title = title;
    if (bridge.isInApp()) {
      bridge.setWebviewTitle(title);
    }
  }, [title]);
}

interface WxConfigData {
  appId: string;
  timestamp: number;
  nonceStr: string;
  signature: string;
  jsApiList: string[];
}

/**
 * 调用某个页面URL的 wx.config 初始化
 * @param url {string} 当前页面URL
 */
export function useWxConfig(url: string = window.location.href) {
  if (url.indexOf('#') >= 0) {
    // 过滤掉#部分
    url = url.substring(0, url.indexOf('#'));
  }
  useLayoutEffect(function () {
    service.get(`/wx/requestWxConfigData?url=${encodeURIComponent(url)}`)
      .then(function (out) {
        console.log('获取到wx.config的参数', out);
        if (out.status === 200 && out.data.status === 0) {
          const realData: WxConfigData = out.data.data;
          wx.config(realData);
        }
      })
      .catch(function (err: Error) {
        console.error(err);
        alert('初始化微信SDK失败');
      });
  }, [url]);
}

interface EchartsWrapper {
  init: typeof echarts.init;
}
let echartsGlobal: EchartsWrapper | null = null;

/**
 * 提供 echarts 库代码的动态加载
 */
export function useEcharts() {
  const [echarts, setEcharts] = useState<EchartsWrapper | null>(echartsGlobal);
  useEffect(function () {
    if (echartsGlobal) {
      return;
    }
    import('echarts').then(function (echarts) {
      echartsGlobal = echarts.default as unknown as EchartsWrapper;
      setEcharts(echartsGlobal);
    });
  }, []);
  return echarts;
}

/**
 * 读取当前的URL中query部分
 */
export function useLocationQuery() {
  const [query] = useState<ParsedQuery>(function () {
    return qs.parse(window.location.search.substring(1)) || {};
  });
  return query;
}

export function useIsApp() {
  const [isApp, setIsApp] = useState(false);
  useEffect(() => {
    setIsApp(bridge.isInApp());
  }, []);
  return {
    isApp,
  };
}

type Hidden = keyof Pick<Document, 'hidden' | 'msHidden' | 'webkitHidden'>
type VisibilityChange = keyof Pick<
  DocumentEventMap,
  'visibilitychange' | 'msvisibilitychange' | 'webkitvisibilitychange'
>
let hidden: Hidden | undefined;
let visibilityChange: VisibilityChange | undefined;

export function usePageVisibility() {
  if (typeof document.hidden !== 'undefined') {
    hidden = 'hidden';
    visibilityChange = 'visibilitychange';
  } else if (typeof document.msHidden !== 'undefined') {
    hidden = 'msHidden';
    visibilityChange = 'msvisibilitychange';
  } else if (typeof document.webkitHidden !== 'undefined') {
    hidden = 'webkitHidden';
    visibilityChange = 'webkitvisibilitychange';
  }
  const initialHidden = hidden ? document[hidden] : false;
  const [visibilityStatus, setVisibilityStatus] = useState<boolean>(initialHidden);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (hidden) {
        setVisibilityStatus(document[hidden]);
        console.log(`${visibilityChange} callback`, hidden, document[hidden]);
      }
    }
    if (visibilityChange) document.addEventListener(visibilityChange, handleVisibilityChange, false);
    return () => {
      if (visibilityChange) document.removeEventListener(visibilityChange, handleVisibilityChange);
    };
  }, [])

  return visibilityStatus;
}
